import { toCsv } from './ledgerExport';

// Issue #465 / #422, Session 78q. The blank import templates for the two
// master-data sets whose importers do not exist yet.
//
// WHY THESE EXIST BEFORE THEIR IMPORTERS DO, and the reasoning is Colby's:
// the typing is the long pole and the importer is the short one. If the columns
// are right, data entry can start now rather than waiting for the build. The
// item and vendor-pricing importers are scheduled after this session.
//
// WHY THE CONTRACT LIVES IN CODE RATHER THAN IN THE FILE ITSELF. #465 is what
// happens otherwise: an Aug 19 template set sat in docs/import-templates/
// looking exactly as official as the real one, carrying 6 columns against the
// importer's 16 and no QuickBooks name at all. Somebody could have typed sixty
// vendors into it and lost every address, every payment term and the one field
// that decides whether linking suppliers in QuickBooks is one press or fifty.
// So the contract is here, the files in docs/templates/ are GENERATED from it,
// and masterDataTemplates.test.js fails the build if the two ever disagree.
// That is the same guard vendorImportTemplate.test.js puts on the vendor file,
// and it is there for the same reason: once a template is in somebody's inbox
// it is beyond our reach.
//
// UNITS ARE DELIBERATELY NOT ENUMERATED IN THIS FILE. Stocking units and
// package types are DATA, not code -- rows in units_of_measure, editable in
// Company Settings by an executive and addable by purchasing without leaving an
// order. Hard-coding them here would create a copy that drifts silently, which
// is the exact defect this module exists to prevent. Read them with
// fetchUnits() from lib/taxonomy.js when a screen needs them; the written guide
// names Company Settings as the authority and dates its snapshot.
//
// Verified against the live schema at Session 78q rather than taken from
// db/SCHEMA_REFERENCE.md alone -- that file still describes inventory_items.uom
// as the inventory_uom ENUM, which Session 78n2 replaced with text foreign-keyed
// to units_of_measure(name).

// ── Items ────────────────────────────────────────────────────────────────────
//
// Sourced from what AddItemModal actually writes, not from the column list:
// name, sku, category_id, uom, vendor_id, description, is_active. The two
// columns it also writes are deliberately absent here --
//
//   image_url   a person cannot type a picture into a spreadsheet.
//   created_at / updated_at   records of what Stede did, not fields anybody
//                             maintains. A column that looks editable and is
//                             ignored on import is worse than an absent one --
//                             VENDOR_COLUMNS says the same thing for the same
//                             reason.
//
// NOT HERE, AND THIS ONE IS A JUDGMENT CALL WORTH KNOWING ABOUT: reorder point
// and reorder quantity. Adding an item creates an inventory_stock row for EVERY
// branch, and reorder levels live on that row -- so they are per-branch, and a
// single column for them would silently mean "Dallas" while reading as though
// it meant the item. That is true until January and wrong afterwards. They are
// set on the item's own screen instead.
//
// 'Stede ID' carries the same contract as the vendor template: blank creates,
// an existing id updates, an id that does not exist yet is REJECTED. Nobody
// looks up the next number; the column exists so that editing an export works.
// `guide` and `help` were added at Session 78u for the .xlsx workbooks (gate
// decisions W1-W18). They live HERE, beside the column they describe, for the
// same reason the headers do: a second copy of this wording in the workbook
// builder is a second thing to keep in step, and #465 is the record of what a
// drifted copy costs. `guide` is the long form on the How-To sheet; `help` is
// the short form that pops up on the cell you are typing in (W7).
export const ITEM_COLUMNS = [
  { header: 'Stede ID', key: 'id', kind: 'key',
    needed: 'Leave blank',
    guide: 'Only used when editing items Stede exported. Blank means create a new item.',
    help: 'Leave blank. Only used when editing items Stede exported.' },
  { header: 'Item name', key: 'name', kind: 'text', required: true,
    needed: 'Required',
    guide: 'What people call it. Dibond 3mm White 4x8',
    help: 'Required. What people call it, e.g. Dibond 3mm White 4x8' },
  { header: 'SKU', key: 'sku', kind: 'text',   // OPTIONAL since #471 -- see below
    needed: 'Optional',
    guide: 'Your own part number, if you have one. Leave it blank and Stede writes one for you from the category and the name.',
    help: 'Optional. Leave blank and Stede writes one for you.' },
  { header: 'Category', key: 'category_id', kind: 'category', required: true,
    needed: 'Required',
    guide: 'Pick from the drop-down. These are your subcategories from Company Settings.',
    help: 'Required. Pick from the drop-down.' },
  { header: 'Stocking unit', key: 'uom', kind: 'unit', required: true,
    needed: 'Required',
    guide: 'How you count it on the shelf: sheet, roll, square foot. Pick from the drop-down.',
    help: 'Required. How you count it on the shelf.' },
  { header: 'Description', key: 'description', kind: 'text',
    needed: 'Optional',
    guide: 'Anything that helps somebody pick the right one.',
    help: 'Optional. Anything that helps somebody pick the right one.' },
  { header: 'Primary vendor', key: 'vendor_id', kind: 'vendor',
    needed: 'Optional',
    guide: 'Who you usually buy it from. The vendor must already be in Stede.',
    help: 'Optional. The vendor must already be in Stede.' },
  { header: 'Active', key: 'is_active', kind: 'bool',
    needed: 'Optional',
    guide: 'Yes or No. Blank means yes.',
    help: 'Yes or No. Blank means yes.' },
];

// SKU: REQUIRED until Session 78s.3, now OPTIONAL. Gate decision S14 of
// docs/gates/Stede_SkuGeneration_Gate_471.html, and this is a change of
// CONTRACT rather than of validation -- read it before touching the line above.
//
// The superseded reasoning was sound on its own terms and is kept because it
// explains what had to change for the new rule to be safe: "SKU is marked
// required even though the database allows it to be null, because AddItemModal
// refuses to save without one and because the vendor-pricing sheet below finds
// items BY SKU. An item with no SKU cannot be priced by import at all, so
// accepting one here would produce rows nothing can attach to."
//
// Both halves of that are still true, and neither forces the column any more,
// because Stede now MAKES a SKU when one is missing:
//
//   BLANK MEANS GENERATE, never "leave it empty". The importer builds the code
//   from the row's own Category and Item name with generateSkuForCategory(),
//   the same function the Add-item form and the promotion screen call -- so an
//   imported item and a hand-added one get identical codes, and there is no
//   third rule to keep in step. Every row still ends up with a SKU, which is
//   what the paragraph above actually depends on.
//
//   A FILLED CELL IS STILL HONOURED, and is not second-guessed. Somebody who
//   already has a code for an item types it, and gate decision S8 freezes it
//   from then on.
//
// This is why #471's gate had to run BEFORE Session 78t's item importer. This
// file is a contract that has already been generated and may already have been
// emailed, and #465 is the record of what it costs to change one after it has
// left the building. Making the column optional after 78t shipped would have
// meant amending a template in somebody's inbox.
//
// The generation itself belongs to 78t, which builds the item importer. What
// changes HERE is only the contract and the guidance the template carries.

// ── Vendor catalog ───────────────────────────────────────────────────────────
//
// REVISED at Session 78s by gate decisions M3 and M4, which SUPERSEDE the
// contract 78q shipped. Eight columns before and eight after, but three change
// meaning and the required set moves. Read the next paragraph before touching
// this -- the change is conceptual, not cosmetic.
//
// 78q called this "vendor pricing" and assumed every row attaches to something
// D&R stocks. Colby overturned that premise at 78r: the rows Jennifer loads are
// the VENDOR'S products, and most of them are things D&R does not stock. That
// is the whole point -- it is a hedge, so that when a manager's "one-time"
// order recurs, the part number and the price are already there.
//
// The schema was ready for this the whole time: vendor_items.item_id is
// NULLABLE, and PurchasingQueuePage.js already writes exactly such a row every
// time an off-catalog request is marked ordered. So this exposes a capability
// rather than inventing one. Fishbowl and Coupa draw the same line; NetSuite
// and Limble fold vendor lines into the item row and both document a way to
// lose data doing it.
//
// THE FILE NAME STILL SAYS "Pricing" -- Stede_Vendor_Pricing_Import_Template.csv
// -- deliberately. The gate revises the contract and does not rename the file,
// and the name is the one referenced by the written guide and the unsent email.
// The constant is named for what the gate calls it; the file is named for what
// people will look for.
//
// WHAT CHANGED, and why each one:
//
//   Vendor part number   optional -> REQUIRED, and it is now THE KEY. M3.
//                        A blank one is a REFUSAL, never a skip. Fishbowl
//                        silently skips rows with a blank part number, which
//                        is the most dangerous behaviour found anywhere in the
//                        research: you believe four hundred rows loaded.
//
//   Item name            reference (ignored) -> 'Product description', REQUIRED
//                        and actually written, to off_catalog_description. On a
//                        row for something D&R does not stock, this is the only
//                        human-readable name the product has. 78q could leave
//                        it unread because every row had a SKU to name it; once
//                        the SKU is optional it cannot.
//
//   Item SKU             required -> OPTIONAL. M4. Blank means the vendor sells
//                        this and we do not stock it, so the row stands alone.
//                        Filled and matching links it to that item. Filled and
//                        matching NOTHING is a refusal, not a silent vendor-only
//                        row -- otherwise a typo quietly becomes an orphan
//                        nobody can find again.
//
// NO 'Stede ID' COLUMN, unchanged and still deliberate. The database enforces a
// natural key: UNIQUE (vendor_id, item_id) for stocked rows, and as of Session
// 78s a partial unique index on (vendor_id, lower(vendor_item_number)) for the
// vendor's-own rows. A surrogate id would be a second way to name the same row
// and a second way to get it wrong.
//
// THE KEY IS CASE-INSENSITIVE, and the importer must match it that way. That
// index compares on lower(vendor_item_number), so an existence lookup using a
// case-sensitive equality would find nothing, decide the row is new, attempt an
// insert, and have the database refuse it -- turning a quiet update into a hard
// error the person importing cannot act on.
//
// THERE IS NO UNIT COST COLUMN AND THERE MUST NEVER BE ONE. vendor_items.unit_cost
// is a GENERATED column -- vendor_price / pack_qty, computed by Postgres. Writing
// it is not merely redundant, it is refused. This is also the single most
// misunderstood thing about the sheet: people pre-divide the price. If Grimco
// sells a bundle of 5 sheets for $247.50, that is 247.50 and 5, not 49.50 and 5.
// Wanting a "price each" column is the signal that the other two are wrong.
export const VENDOR_CATALOG_COLUMNS = [
  { header: 'Vendor name', key: 'vendor_id', kind: 'vendor', required: true,
    needed: 'Required',
    guide: 'Who sells it. The vendor must already be in Stede. Pick from the drop-down.',
    help: 'Required. Pick from the drop-down.' },
  { header: 'Vendor part number', key: 'vendor_item_number', kind: 'text', required: true,
    needed: 'Required',
    guide: "The vendor's own code for this product. It is how Stede matches the row, so a blank one is refused rather than skipped.",
    help: "Required. The vendor's own code. A blank one is refused." },
  { header: 'Product description', key: 'off_catalog_description', kind: 'text', required: true,
    needed: 'Required',
    guide: "What it is called on the vendor's price list. On a row for something D&R does not stock, this is the only readable name the product has.",
    help: "Required. The product's name on the vendor's list." },
  { header: 'Item SKU', key: 'item_id', kind: 'item',
    needed: 'Optional',
    guide: 'Fill this in only if D&R stocks the item. Blank means the vendor sells it and we do not, which is fine. A SKU that matches nothing is refused.',
    help: 'Optional. Only if D&R stocks it. Blank is fine.' },
  { header: 'Price per pack', key: 'vendor_price', kind: 'money', required: true,
    needed: 'Required',
    guide: 'The price of the WHOLE pack, not per unit. A bundle of 5 sheets at $247.50 is 247.50 here and 5 in the next column.',
    help: 'Required. The WHOLE pack price, not per unit. A bundle of 5 at $247.50 is 247.50.' },
  { header: 'Units per pack', key: 'pack_qty', kind: 'integer', required: true,
    needed: 'Required',
    guide: 'How many come in one pack. The bundle of 5 sheets is 5. Stede works out the price each.',
    help: 'Required. How many in one pack. Stede works out the price each.' },
  { header: 'Package type', key: 'pack_unit', kind: 'unit',
    needed: 'Optional',
    guide: 'What the pack is — a bundle, a roll, a case. Pick from the drop-down.',
    help: 'Optional. What the pack is. Pick from the drop-down.' },
  { header: 'Preferred vendor', key: 'is_preferred', kind: 'bool',
    needed: 'Optional',
    guide: 'Yes or No. Ignored on a row for something D&R does not stock, because there is no item to prefer a vendor for.',
    help: 'Yes or No. Ignored where D&R does not stock the item.' },
];

// COLUMN ORDER IS THE TYPING ORDER, not the schema order: the two things that
// identify the row first, then the optional link to our catalog, then the
// numbers. Somebody working down four hundred rows of a vendor's price list has
// the vendor's own part number and description in front of them and may not
// have a Stede SKU at all.
//
// 'Preferred vendor' is meaningless on a vendor's-own row -- there is no item to
// prefer a vendor FOR -- so it is ignored there, and the preview says so rather
// than failing. Where it does apply, two rows both claiming preferred for one
// item is a REFUSAL naming both rows. NetSuite takes the last one silently, on
// a field that decides where a reorder goes. M10.

// ── The files themselves ─────────────────────────────────────────────────────
//
// Headers only. A blank template that imports something when handed over
// unchanged is a trap, so there are no example rows -- not even one marked
// EXAMPLE, which the Aug 19 set had and which relied on somebody remembering to
// delete it.
//
// toCsv() prepends the BOM and joins with CRLF. The BOM is what stops Excel
// mangling an accented name on the round trip, and it is the same function the
// real exports use, so the templates cannot drift from them in encoding either.
//
// A blank template needs ONLY the header row: findHeaderRow() locates the
// headers by content, so the provenance block the exports write above theirs is
// optional rather than required.
export function templateCsv(columns) {
  return toCsv([], columns.map(c => c.header), []);
}

export const TEMPLATES = [
  {
    file: 'Stede_Item_Import_Template.csv',
    label: 'Item catalog',
    columns: ITEM_COLUMNS,
  },
  {
    file: 'Stede_Vendor_Pricing_Import_Template.csv',
    label: 'Vendor catalog',
    columns: VENDOR_CATALOG_COLUMNS,
  },
];
